import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  login as apiLogin,
  register as apiRegister,
  setStoredToken,
  getStoredToken,
  getMyProfile,
  updateUser as apiUpdateUser,
} from '../api';

const AUTH_KEY = 'market_mavericks_auth';
const USERS_KEY = 'market_mavericks_users';
const COURSES_KEY = 'market_mavericks_courses';
const ENROLLMENTS_KEY = 'market_mavericks_enrollments';
const CMS_TERMS_KEY = 'market_mavericks_cms_terms';
const CMS_PRIVACY_KEY = 'market_mavericks_cms_privacy';
const CMS_ABOUT_KEY = 'market_mavericks_cms_about';
const CMS_FAQ_KEY = 'market_mavericks_cms_faq';
const CMS_BASIC_QUIZ_KEY = 'market_mavericks_cms_basic_quiz';
const COURSE_RATINGS_KEY = 'market_mavericks_course_ratings';

const getStored = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const getStoredSession = (key, fallback) => {
  try {
    const v = sessionStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const setStored = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    if (e.name === 'QuotaExceededError' || e.code === 22) {
      const err = new Error('Storage limit reached. Use links (URLs) instead of file upload for videos and images.');
      err.name = 'QuotaExceededError';
      throw err;
    }
    throw e;
  }
};

const setStoredSession = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    if (e.name === 'QuotaExceededError' || e.code === 22) {
      const err = new Error('Storage limit reached.');
      err.name = 'QuotaExceededError';
      throw err;
    }
    throw e;
  }
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = getStored(AUTH_KEY, null) || getStoredSession(AUTH_KEY, null);
    return stored?.user || null;
  });
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const u = await getMyProfile();
        setUser(u);
        if (localStorage.getItem(AUTH_KEY)) {
          setStored(AUTH_KEY, { user: u });
        } else if (sessionStorage.getItem(AUTH_KEY)) {
          setStoredSession(AUTH_KEY, { user: u });
        }
      } catch {
        /* stale token or network; keep stored user */
      } finally {
        setLoading(false);
      }
    })();
  }, []);


  /** Login via API. When rememberMe is true, token and user are stored in localStorage; otherwise in sessionStorage (session only). */
  const login = async (email, password, rememberMe = true) => {
    const result = await apiLogin({ email: (email || '').trim(), password });
    if (!result.success) return result;
    setStoredToken(result.token, rememberMe);
    if (rememberMe) {
      setStored(AUTH_KEY, { user: result.user });
      sessionStorage.removeItem(AUTH_KEY);
    } else {
      setStoredSession(AUTH_KEY, { user: result.user });
      localStorage.removeItem(AUTH_KEY);
    }
    setUser(result.user);
    return { success: true, user: result.user };
  };

  const logout = () => {
    setUser(null);
    setStoredToken(null);
    localStorage.removeItem(AUTH_KEY);
    sessionStorage.removeItem(AUTH_KEY);
  };

  /** Register via API; then auto-login (token + user stored in localStorage). */
  const registerUser = async (firstName, lastName, email, password, number = '') => {
    const result = await apiRegister({
      first_name: (firstName || '').trim(),
      last_name: (lastName || '').trim(),
      number: (number || '').trim(),
      email: (email || '').trim(),
      password,
    });
    if (!result.success) return result;
    setStoredToken(result.token);
    setStored(AUTH_KEY, { user: result.user });
    setUser(result.user);
    return { success: true };
  };

  const getUsers = () => getStored(USERS_KEY, []);
  const setUsers = (users) => setStored(USERS_KEY, users);

  const updateUserProfile = async (updates) => {
    if (!user?.id) return;
    const payload = {};
    if (updates.name !== undefined) {
      const parts = String(updates.name).trim().split(/\s+/);
      payload.first_name = parts[0] || user.firstName || '';
      payload.last_name = parts.slice(1).join(' ') || user.lastName || '';
    }
    if (updates.mobile !== undefined) payload.number = updates.mobile;
    if (updates.pan !== undefined) payload.pan = updates.pan;
    if (updates.experience !== undefined) payload.experience = updates.experience;
    if (updates.risk !== undefined) payload.risk = updates.risk;
    if (updates.broker !== undefined) payload.broker = updates.broker;
    if (updates.profileImage !== undefined) payload.profile_image = updates.profileImage;
    if (updates.subscriptionPlan !== undefined) payload.subscription_plan = updates.subscriptionPlan;
    const updated = await apiUpdateUser(user.id, payload);
    setUser(updated);
    setStored(AUTH_KEY, { user: updated });
  };

  const getCourses = () => getStored(COURSES_KEY, []);
  const setCourses = (courses) => setStored(COURSES_KEY, courses);

  /** CMS pages: terms, privacy, about, faq, basic_quiz. */
  const getCmsPage = (key) => {
    if (key === 'terms') return getStored(CMS_TERMS_KEY, null);
    if (key === 'privacy') return getStored(CMS_PRIVACY_KEY, null);
    if (key === 'about') return getStored(CMS_ABOUT_KEY, null);
    if (key === 'faq') return getStored(CMS_FAQ_KEY, null);
    if (key === 'basic_quiz') return getStored(CMS_BASIC_QUIZ_KEY, null);
    return null;
  };
  const setCmsPage = (key, value) => {
    if (key === 'terms') setStored(CMS_TERMS_KEY, value);
    else if (key === 'privacy') setStored(CMS_PRIVACY_KEY, value);
    else if (key === 'about') setStored(CMS_ABOUT_KEY, value);
    else if (key === 'faq') setStored(CMS_FAQ_KEY, value);
    else if (key === 'basic_quiz') setStored(CMS_BASIC_QUIZ_KEY, value);
  };

  /** Enrollments: { [userId]: [courseId, ...] } */
  const getEnrollments = () => getStored(ENROLLMENTS_KEY, {});
  const setEnrollments = (data) => setStored(ENROLLMENTS_KEY, data);

  const getEnrolledCourseIds = (userId) => {
    const data = getEnrollments();
    const local = Array.isArray(data[userId]) ? data[userId] : [];
    let fromApi = [];
    let fromSubscription = [];
    if (user?.id && String(user.id) === String(userId)) {
      const pc = user.purchasedCourseIds;
      fromApi = Array.isArray(pc) ? pc.map(String) : [];
      const sf = user.subscriptionFreeCourseIds;
      fromSubscription = Array.isArray(sf) ? sf.map(String) : [];
    }
    return [...new Set([...fromApi, ...fromSubscription, ...local.map(String)])];
  };

  const enrollInCourse = (userId, courseId) => {
    const data = getEnrollments();
    const list = data[userId] || [];
    if (list.includes(courseId)) return;
    data[userId] = [...list, courseId];
    setEnrollments(data);
  };

  /** Course ratings: [{ id, userId, courseId, rating (1-5), review?, createdAt }] */
  const getCourseRatings = () => getStored(COURSE_RATINGS_KEY, []);
  const setCourseRatings = (list) => setStored(COURSE_RATINGS_KEY, list);

  const getRatingByUserForCourse = (userId, courseId) => {
    const list = getCourseRatings();
    return list.find((r) => String(r.userId) === String(userId) && String(r.courseId) === String(courseId)) || null;
  };

  const getRatingsForCourse = (courseId) => {
    return getCourseRatings().filter((r) => String(r.courseId) === String(courseId));
  };

  const submitCourseRating = (userId, courseId, rating, review = '') => {
    const list = getCourseRatings();
    const numRating = Math.min(5, Math.max(1, Number(rating) || 0));
    const existing = list.findIndex((r) => String(r.userId) === String(userId) && String(r.courseId) === String(courseId));
    const entry = {
      id: existing >= 0 ? list[existing].id : Date.now().toString(),
      userId,
      courseId,
      rating: numRating,
      review: String(review || '').trim(),
      createdAt: existing >= 0 ? list[existing].createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const next = existing >= 0 ? [...list.slice(0, existing), entry, ...list.slice(existing + 1)] : [...list, entry];
    setCourseRatings(next);
  };

  const removeCourseRating = (ratingId) => {
    const list = getCourseRatings().filter((r) => r.id !== ratingId);
    setCourseRatings(list);
  };

  const isAdmin = user?.role === 'admin';

  /** Reload profile from API (e.g. after course purchase). Keeps the same storage (local vs session) as login. */
  const refreshUser = async () => {
    if (!getStoredToken()) return null;
    try {
      const u = await getMyProfile();
      setUser(u);
      if (localStorage.getItem(AUTH_KEY)) {
        setStored(AUTH_KEY, { user: u });
      } else if (sessionStorage.getItem(AUTH_KEY)) {
        setStoredSession(AUTH_KEY, { user: u });
      }
      return u;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        registerUser,
        refreshUser,
        isAdmin,
        getUsers,
        setUsers,
        updateUserProfile,
        getCourses,
        setCourses,
        getCmsPage,
        setCmsPage,
        getEnrollments,
        getEnrolledCourseIds,
        enrollInCourse,
        getCourseRatings,
        getRatingByUserForCourse,
        getRatingsForCourse,
        submitCourseRating,
        removeCourseRating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
